import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { useFraudManagement } from '@/hooks/useFraudManagement';
import { useAuth } from '@/contexts/AuthContext';

const FraudScoreDisplay = () => {
  const { profile } = useAuth();
  const { fraudScore, loading } = useFraudManagement();

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Fraud Score
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getScoreBadgeVariant = (score: number) => {
    if (score >= 80) return 'destructive';
    if (score >= 50) return 'secondary';
    return 'default';
  };

  const getScoreIcon = (score: number) => {
    if (score >= 80) return <AlertTriangle className="w-4 h-4" />;
    if (score >= 50) return <Shield className="w-4 h-4" />;
    return <CheckCircle className="w-4 h-4" />;
  };

  const getScoreDescription = (score: number) => {
    if (score >= 80) return 'High risk - Activity under review';
    if (score >= 50) return 'Medium risk - Monitoring required';
    return 'Low risk - Good standing';
  };

  const affiliateScore = fraudScore?.find(s => s.affiliate_id === profile?.affiliate_id);
  const score = affiliateScore?.score || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Fraud Score
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {getScoreIcon(score)}
              <span className={`text-2xl font-bold ${getScoreColor(score)}`}>
                {score}/100
              </span>
            </div>
            <Badge variant={getScoreBadgeVariant(score)}>
              {getScoreDescription(score)}
            </Badge>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${
                score >= 80 ? 'bg-red-600' : score >= 50 ? 'bg-yellow-600' : 'bg-green-600'
              }`}
              style={{ width: `${Math.min(score, 100)}%` }}
            />
          </div>

          <div className="text-sm text-gray-600">
            <p>
              Your fraud score is based on various factors including conversion patterns, 
              velocity, and compliance with affiliate guidelines.
            </p>
          </div>

          {affiliateScore?.last_updated && (
            <p className="text-xs text-gray-500">
              Last updated: {new Date(affiliateScore.last_updated).toLocaleDateString()}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default FraudScoreDisplay;